import type { ReactNode } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';

export type PillTone = 'green' | 'red' | 'amber' | 'ink' | 'violet' | 'slate';

interface StatusPillProps {
  tone: PillTone;
  icon?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md';
}

// Replacement for plain antd Tag. Better:
//  - Subtle background tint (not just colored text)
//  - Thin border in the same accent
//  - Optional dot or icon
//  - Aware of dark mode
//  - Tabular numerics so badges line up vertically
//
// Use sparingly — too many tones in one row = visual noise.

const TONE_LIGHT: Record<PillTone, { bg: string; fg: string; border: string; dot: string }> = {
  green: { bg: `${colors.green[100]}`, fg: colors.green[700], border: `${colors.green[600]}33`, dot: colors.green[600] },
  red: { bg: `${colors.red[100]}`, fg: colors.red[700], border: `${colors.red[600]}33`, dot: colors.red[600] },
  amber: { bg: `${colors.amber[100]}`, fg: colors.amber[700], border: `${colors.amber[600]}33`, dot: colors.amber[600] },
  ink: { bg: `${colors.ink[50]}`, fg: colors.ink[700], border: `${colors.ink[500]}33`, dot: colors.ink[600] },
  violet: { bg: `${colors.violet[50]}`, fg: colors.violet[700], border: `${colors.violet[500]}33`, dot: colors.violet[600] },
  slate: { bg: `${colors.slate[100]}`, fg: colors.slate[600], border: `${colors.slate[300]}`, dot: colors.slate[500] },
};

const TONE_DARK: Record<PillTone, { bg: string; fg: string; border: string; dot: string }> = {
  green: { bg: `${colors.green[700]}40`, fg: colors.green[100], border: `${colors.green[500]}55`, dot: colors.green[500] },
  red: { bg: `${colors.red[700]}40`, fg: colors.red[100], border: `${colors.red[500]}55`, dot: colors.red[500] },
  amber: { bg: `${colors.amber[700]}40`, fg: colors.amber[100], border: `${colors.amber[500]}55`, dot: colors.amber[500] },
  ink: { bg: `${colors.ink[700]}40`, fg: colors.ink[100], border: `${colors.ink[500]}55`, dot: colors.ink[500] },
  violet: { bg: `${colors.violet[700]}40`, fg: colors.violet[100], border: `${colors.violet[500]}55`, dot: colors.violet[500] },
  slate: { bg: `${colors.slate[700]}`, fg: colors.slate[200], border: `${colors.slate[600]}`, dot: colors.slate[400] },
};

export const StatusPill = observer(function StatusPill({
  tone,
  icon,
  children,
  size = 'sm',
}: StatusPillProps) {
  const { themeStore } = useStore();
  const palette = themeStore.mode === 'dark' ? TONE_DARK[tone] : TONE_LIGHT[tone];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: size === 'md' ? '4px 10px' : '2px 8px',
        fontSize: size === 'md' ? 12 : 11,
        fontWeight: 600,
        lineHeight: 1.4,
        borderRadius: 999,
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {icon ? (
        <span style={{ display: 'inline-flex', color: palette.dot, fontSize: 10 }}>{icon}</span>
      ) : (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: palette.dot,
            display: 'inline-block',
          }}
          aria-hidden
        />
      )}
      {children}
    </span>
  );
});
